import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Countdown from 'react-countdown-now';
import { Link } from 'react-router-dom';
import Prize from 'containers/Auctions/components/Prize';
import withSounds from 'containers/withSounds';
import GameIcon from 'components/GameIcon';
import TruncatingTextBlock from 'components/TruncatingTextBlock';
import TimeSync from 'utils/TimeSync';
import TimeSyncWrapper from 'utils/TimeSync/TimeSyncWrapper';
import { LG_BREAKPOINT_AND_UP } from 'utils/constants';
import { fromIso9075DateString, makeDate } from 'utils/dates';
import { formatDate, padNumber } from 'utils/formatting';
import { tournamentShape } from 'utils/shapes';
import { replaceAllStrings } from 'utils/strings';
import {
  TOURNAMENT_LIST_EMPTY_SUBTEXT,
  TOURNAMENT_LIST_EMPTY_TITLE,
  TOURNAMENT_STATUS_COMPLETED,
  TOURNAMENT_STATUS_IN_PROGRESS,
  TOURNAMENT_STATUS_UPCOMING,
  isTournamentQueueAvailable
} from 'utils/tournaments';
import useMediaQuery from 'utils/useMediaQuery';
import LeaderboardSummary from './LeaderboardSummary';

const MAX_DESCRIPTION = 100;
const LinkWithSounds = withSounds({ mouseEnterSound: true })(Link);

function TournamentListGroup({ status, tournaments, hideButtons }) {
  const showMobileLayout = !useMediaQuery(LG_BREAKPOINT_AND_UP);

  function renderItem(tournament) {
    return showMobileLayout ? (
      <div
        className={`tournament-list-item ${tournament.status.toLowerCase()}`}
        key={tournament.id}
      >
        <div className="tournament-details-top-row">
          {renderImage(tournament)}
          <div className="tournament-title-and-time mobile">
            {renderTopDate(tournament)}
            {renderTitle(tournament)}
            {renderDescription(tournament)}
          </div>
        </div>
        <div className="tournament-details mobile">
          {renderTimer(tournament)}
          {renderDate(tournament)}
          {renderButton(tournament)}
        </div>
      </div>
    ) : (
      <div
        className={`tournament-list-item ${tournament.status.toLowerCase()}`}
        key={tournament.id}
      >
        {renderImage(tournament)}
        <div className="tournament-details">
          {renderTopDate(tournament)}
          {renderTitle(tournament)}
          {renderDescription(tournament)}
          {renderTimer(tournament)}
          {renderDate(tournament)}
          {renderDuration(tournament)}
          {renderButton(tournament)}
        </div>
        {renderLeaderboard(tournament)}
      </div>
    );
  }

  function renderImage(tournament) {
    return (
      <div className="tournament-image">
        {tournament.game && (
          <GameIcon
            game={tournament.game}
            alt={tournament.game.displayName}
            badge={tournament.game.badge}
          />
        )}
      </div>
    );
  }

  function renderTopDate(t) {
    if (t.status === TOURNAMENT_STATUS_COMPLETED) {
      return null;
    }

    const startDateDisplay = formatDate(makeDate(t.startTime), 'fullCompact');

    return <div className="start-date">{startDateDisplay}</div>;
  }

  function renderTitle(tournament) {
    return (
      <div className="tournament-title-container">
        <div className="tournament-name">{tournament.name}</div>
        {tournament.status === TOURNAMENT_STATUS_COMPLETED &&
          !!showMobileLayout &&
          renderDuration(tournament)}
      </div>
    );
  }

  function renderTimer(t) {
    if (t.status === TOURNAMENT_STATUS_COMPLETED) {
      return null;
    }

    const isInProgress = t.status === TOURNAMENT_STATUS_IN_PROGRESS;
    const tournamentTime = isInProgress ? t.endTime : t.startTime;
    const tournamentMillis = fromIso9075DateString(tournamentTime).valueOf();
    const countdown = TimeSync.toLocalTime(tournamentMillis);
    const countdownLabel = isInProgress ? 'Ends in:' : 'Starts in:';

    return (
      <div className="tournament-timer">
        <div className="tournament-duration">
          <div className="detail-label">Duration:</div>
          <div>{getTournamentDuration(t) + ' minutes'}</div>
        </div>
        <div className="countdown-wrapper">
          <div className="detail-label">{countdownLabel}</div>
          <div className="countdown notranslate">
            <Countdown
              key={countdownLabel}
              date={countdown}
              renderer={countdownRenderer}
            />
          </div>
        </div>
      </div>
    );
  }

  function renderDescription(tournament) {
    const { name, description, winningPlaces } = tournament;
    const prizes =
      winningPlaces?.winningPlaces?.map(place => place.prize) || [];
    const hasPrizes = prizes.length > 0;
    return (
      <div className="tournament-description">
        <div className="detail-label">
          Prize:
          {hasPrizes ? (
            <Prize
              prizes={prizes}
              auctionName={name}
              auctionDescription={description}
              openEnabled
            />
          ) : (
            <span>-</span>
          )}
        </div>
        <TruncatingTextBlock
          className="description"
          text={description}
          tooltipClass="tournament-list-description-tooltip"
          maxLength={MAX_DESCRIPTION}
          maxHeight="6em"
          container={this}
          placement="top"
        />
      </div>
    );
  }

  function renderDate(t) {
    if (t.status !== TOURNAMENT_STATUS_COMPLETED) {
      return null;
    }
    const date = fromIso9075DateString(t.startTime);
    const startDate = replaceAllStrings(
      formatDate(date, 'long'),
      ' ',
      '\u00A0'
    );
    const startTime = replaceAllStrings(
      formatDate(date, 'localtime'),
      ' ',
      '\u00A0'
    );

    return (
      <div className="tournament-date">
        <div className="detail-label">Date:</div>
        <div>{startDate + ', ' + startTime}</div>
      </div>
    );
  }

  function getTournamentDuration(t) {
    const startTime = moment(fromIso9075DateString(t.startTime));
    const endTime = moment(fromIso9075DateString(t.endTime));
    if (startTime.isValid() && endTime.isValid()) {
      return endTime.diff(startTime, 'minutes');
    } else {
      return 0;
    }
  }

  function renderDuration(t) {
    if (t.status !== TOURNAMENT_STATUS_COMPLETED) {
      return null;
    }
    return (
      <div className="tournament-duration">
        <div className="detail-label">Duration:</div>
        <div>{getTournamentDuration(t) + ' minutes'}</div>
      </div>
    );
  }
  function renderButton(t) {
    const isUpcoming = t.status === TOURNAMENT_STATUS_UPCOMING;
    if (
      t.status === TOURNAMENT_STATUS_COMPLETED ||
      (isUpcoming && !isTournamentQueueAvailable(t))
    ) {
      return null;
    }

    if (hideButtons) {
      return (
        !isUpcoming && (
          <div className="button-replacement-text">In Progress</div>
        )
      );
    }

    const btnText = !t.userInGroup
      ? 'Watch Now'
      : isUpcoming
      ? 'Join Early'
      : 'Join';
    return (
      <LinkWithSounds className="tournament-button" to={`/tournament/${t.id}`}>
        {btnText}
      </LinkWithSounds>
    );
  }

  function renderLeaderboard(t) {
    return (
      t.status === TOURNAMENT_STATUS_COMPLETED &&
      t.leaderBoard &&
      t.leaderBoard.rankings && (
        <div className="completed-tournament-leaderboard">
          <p className="detail-label">Top Scores</p>
          <LeaderboardSummary tournament={t} />
        </div>
      )
    );
  }

  if (!tournaments) {
    return null;
  } else if (tournaments.length === 0) {
    return (
      <div className="tournament-list-empty">
        <div className="empty-notice-title">
          {TOURNAMENT_LIST_EMPTY_TITLE[status]}
        </div>
        <div className="empty-notice-subtext">
          {TOURNAMENT_LIST_EMPTY_SUBTEXT[status]}
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-list">
      {tournaments.map(tournament => renderItem(tournament))}
    </div>
  );
}

const countdownRenderer = ({ days, hours, minutes, seconds }) => {
  let output = '';
  if (days) {
    output += padNumber(days) + ':';
  }
  if (days || hours) {
    output += padNumber(hours) + ':';
  }
  output += padNumber(minutes) + ':' + padNumber(seconds);
  return <span className="notranslate">{output}</span>;
};

TournamentListGroup.propTypes = {
  status: PropTypes.string,
  tournaments: PropTypes.arrayOf(tournamentShape),
  hideButtons: PropTypes.bool
};

export default TimeSyncWrapper(TournamentListGroup);
